import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { Dialog } from '../../shared/custom-ui';
import { V2Button } from '../../shared/custom-ui/v2-components';
import AddScheduling from './actions/appointment/addScheduling';
import { PlaceOrderModal } from '../Lab/OrderManagement';
import { Icon } from '@ohif/ui';
import { RefreshCw, CalendarCheck, Search, Trash2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import PatientAvatar from '../WorkListTable/components/PatientAvatar';
import PatientsLayout from './components/PatientsLayout';
import ModernPageHeader from '../WorkListTable/components/ModernPageHeader';
import useWorklistScale from '../WorkListTable/hooks/useWorklistScale';
import UnifiedPagination from '../../shared/custom-ui/UnifiedPagination';
import { buildPatientDisplayName } from '../../shared/utils/nameUtils';

const Icons = { RefreshCw, CalendarCheck, Search } as any;
type Patient = any;

export default function PatientsHistory({ }: { onClose?: () => void }) {
    const [patients, setPatients] = useState<Patient[]>([]);
    const [loading, setLoading] = useState(true);
    const [appointOpen, setAppointOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<any>(null);
    const [labOrderPatient, setLabOrderPatient] = useState<any>(null);
    const [patientToDelete, setPatientToDelete] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [nextToken, setNextToken] = useState<string | null>(null);
    const [tokenStack, setTokenStack] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(25);

    // search
    const [mrnQuery, setMrnQuery] = useState('');
    const [nameQuery, setNameQuery] = useState('');
    const mrnDebounceRef = useRef<number>();
    const nameDebounceRef = useRef<number>();

    const navigate = useNavigate();
    const paginationRef = useRef<HTMLDivElement>(null);
    const scale = useWorklistScale(paginationRef);

    useEffect(() => {
        getAllPatients();
    }, []);

    const getAllPatients = async (
        token: string | null = null,
        mrn: string = mrnQuery,
        patientName: string = nameQuery
    ) => {
        setLoading(true);
        try {
            const resp = await ApiServices.getAllPatients({ limit, next_token: token, mrn, patientName });
            setPatients(resp.items || []);
            setNextToken(resp.nextToken || null);
        } catch (error) {
            console.error('Error fetching patients:', error);
            setPatients([]);
            setNextToken(null);
        } finally {
            setLoading(false);
        }
    };

    const refresh = () => {
        setCurrentPage(1);
        setTokenStack([]);
        getAllPatients(null);
    };

    const toggleAppointOpen = (value: boolean | null = null) =>
        setAppointOpen(value !== null ? value : !appointOpen);

    const addNewAppointment = (patient: any, isFromHistory = false) => {
        setSelectedPatient({ ...patient, isFromHistory });
        toggleAppointOpen(true);
    };

    const addNewLabOrder = (patient: any) => {
        setLabOrderPatient(patient);
    };

    const handleDeletePatient = async () => {
        if (!patientToDelete) return;
        const patientId = patientToDelete.PK;
        setIsDeleting(true);
        try {
            await ApiServices.deletePatient(patientId);
            toast.success(`Patient "${buildPatientDisplayName(patientToDelete) || 'Unknown'}" deleted successfully.`);
            setPatients(prev => prev.filter(p => p.PK !== patientId));
            setPatientToDelete(null);
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                'Failed to delete patient. Please try again.'
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const handleNext = (token: string | null) => {
        if (!token) return;
        setTokenStack(prev => [...prev, token]);
        setCurrentPage(prev => prev + 1);
        getAllPatients(token);
    };

    const handlePrev = () => {
        if (currentPage === 1) return;
        setTokenStack(prev => {
            const copy = [...prev];
            copy.pop();
            const prevToken = copy.length ? copy[copy.length - 1] : null;
            getAllPatients(prevToken);
            return copy;
        });
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const onMrnChange = (v: string) => {
        setMrnQuery(v);
        if (mrnDebounceRef.current) window.clearTimeout(mrnDebounceRef.current);
        mrnDebounceRef.current = window.setTimeout(() => {
            setCurrentPage(1);
            setTokenStack([]);
            getAllPatients(null, v, nameQuery);
        }, 500);
    };

    const onNameChange = (v: string) => {
        setNameQuery(v);
        if (nameDebounceRef.current) window.clearTimeout(nameDebounceRef.current);
        nameDebounceRef.current = window.setTimeout(() => {
            setCurrentPage(1);
            setTokenStack([]);
            getAllPatients(null, mrnQuery, v);
        }, 500);
    };

    return (
        <div ref={paginationRef} className="relative">
            {/* ══════ HEADER — stays full size for usability ══════ */}
            <ModernPageHeader
                title="Patients History"
                subtitle="Manage patient records, appointments, and history efficiently."
                actions={
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <V2Button
                            variant="secondary"
                            size="small"
                            leftIcon={<Icons.RefreshCw className="h-4 w-4" />}
                            onClick={refresh}
                            aria-label="Refresh patient list"
                        >
                            Refresh
                        </V2Button>
                        <V2Button
                            variant="primary"
                            size="small"
                            leftIcon={<Icon name="add-plus-icon" className="h-4 w-4" />}
                            onClick={() => navigate('/patients-history/patient-register')}
                        >
                            <span className="hidden sm:inline">Patient Register</span>
                            <span className="sm:hidden">Register</span>
                        </V2Button>
                    </div>
                }
            >
                {/* Search Bar (passed as children) */}
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full max-w-2xl">
                    <div className="relative w-full">
                        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--v2-text-muted)]">
                            <Icons.Search className="h-4 w-4" />
                        </span>
                        <input
                            value={mrnQuery}
                            onChange={e => onMrnChange(e.target.value)}
                            placeholder="Search by MRN..."
                            aria-label="Search by MRN"
                            className="w-full rounded-lg border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-card-inner)] text-[var(--v2-text-primary)]
                             pl-10 pr-10 py-2.5 text-[15px] font-medium focus:outline-none focus:ring-2 focus:ring-[var(--v2-primary-500)]/30
                             placeholder:text-[var(--v2-text-muted)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] transition-colors"
                        />
                        {mrnQuery && (
                            <button
                                type="button"
                                aria-label="Clear MRN search"
                                onClick={() => onMrnChange('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full transition-colors hover:bg-[var(--v2-bg-hover)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text-heading)]"
                                style={{ outline: 'none' }}
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    <div className="relative w-full">
                        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--v2-text-muted)]">
                            <Icons.Search className="h-4 w-4" />
                        </span>
                        <input
                            value={nameQuery}
                            onChange={e => onNameChange(e.target.value)}
                            placeholder="Search by Patient Name..."
                            aria-label="Search by Patient Name"
                            className="w-full rounded-lg border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-card-inner)] text-[var(--v2-text-primary)]
                             pl-10 pr-10 py-2.5 text-[15px] font-medium focus:outline-none focus:ring-2 focus:ring-[var(--v2-primary-500)]/30
                             placeholder:text-[var(--v2-text-muted)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] transition-colors"
                        />
                        {nameQuery && (
                            <button
                                type="button"
                                aria-label="Clear name search"
                                onClick={() => onNameChange('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full transition-colors hover:bg-[var(--v2-bg-hover)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text-heading)]"
                                style={{ outline: 'none' }}
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </ModernPageHeader>

            <div>
                <PatientsLayout
                    data={patients}
                    loading={loading}
                    onRowClick={row => navigate(`/patients-history/${row.PK}`)}
                    onAppointment={row => addNewAppointment(row, true)}
                    onLabOrder={row => addNewLabOrder(row)}
                    onDelete={row => setPatientToDelete(row)}
                    scale={scale}
                />

                {/* PAGINATION */}
                {!loading && patients.length > 0 && (
                    <div className="mt-4" style={{ zoom: scale }}>
                        <UnifiedPagination
                            currentPage={currentPage}
                            onPrevious={handlePrev}
                            onNext={() => handleNext(nextToken)}
                            canGoBack={currentPage > 1}
                            hasMore={!!nextToken}
                        />
                    </div>
                )}
            </div>

            {/* ══════ DELETE CONFIRMATION DIALOG ══════ */}
            <Dialog
                isOpen={!!patientToDelete}
                onClose={() => !isDeleting && setPatientToDelete(null)}
                size="small"
                showCloseButton={false}
                preventCloseOnOverlayClick={isDeleting}
                footer={
                    <div className="flex items-center justify-end gap-3">
                        <V2Button
                            variant="secondary"
                            size="small"
                            onClick={() => setPatientToDelete(null)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </V2Button>
                        <V2Button
                            variant="danger"
                            size="small"
                            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                            onClick={handleDeletePatient}
                            loading={isDeleting}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Deleting…' : 'Delete Patient'}
                        </V2Button>
                    </div>
                }
            >
                {patientToDelete && (
                    <div className="flex flex-col items-center text-center gap-4 py-2">
                        {/* Animated danger icon */}
                        <div
                            className="relative flex h-16 w-16 items-center justify-center rounded-full"
                            style={{ background: 'rgba(239,68,68,0.10)' }}
                        >
                            <div
                                className="absolute inset-0 rounded-full animate-ping"
                                style={{ background: 'rgba(239,68,68,0.15)' }}
                            />
                            <AlertTriangle className="relative h-7 w-7" style={{ color: '#ef4444' }} />
                        </div>

                        {/* Heading */}
                        <div>
                            <h3 className="text-[18px] font-bold text-[var(--v2-text-heading)] mb-1">
                                Delete Patient
                            </h3>
                            <p className="text-[14px] text-[var(--v2-text-muted)] leading-relaxed">
                                This action is permanent and cannot be undone.
                            </p>
                        </div>

                        {/* Patient identity card */}
                        <div
                            className="w-full flex items-center gap-3 rounded-xl px-4 py-3 border"
                            style={{
                                background: 'var(--v2-bg-card-inner)',
                                borderColor: 'var(--v2-border-subtle)',
                            }}
                        >
                            <PatientAvatar
                                name={buildPatientDisplayName(patientToDelete)}
                                mrn={patientToDelete.MRN}
                            />
                            <div className="text-left min-w-0">
                                <p className="font-semibold text-[15px] truncate text-[var(--v2-text-primary)]">
                                    {buildPatientDisplayName(patientToDelete) || 'Unknown Patient'}
                                </p>
                                <p className="text-[13px] text-[var(--v2-text-muted)]">
                                    MRN: {patientToDelete.MRN || 'N/A'}
                                </p>
                            </div>
                        </div>

                        {/* Warning note */}
                        <p className="text-[13px] text-[var(--v2-text-muted)]">
                            Are you sure you want to permanently delete{' '}
                            <span className="font-semibold text-[var(--v2-text-primary)]">
                                {buildPatientDisplayName(patientToDelete) || 'this patient'}
                            </span>
                            ? All associated records will be lost.
                        </p>
                    </div>
                )}
            </Dialog>

            {/* ══════ APPOINTMENT DIALOG (original) ══════ */}
            <Dialog
                isOpen={appointOpen}
                onClose={() => toggleAppointOpen(false)}
                size="xl"
                title="Add Order"
                description="Create an appointment and attach it to the selected patient."
                className="max-h-[70vh]"
            >
                <AddScheduling toggleModal={toggleAppointOpen} modalPatient={selectedPatient} />
            </Dialog>

            {/* ══════ PLACE NEW LAB ORDER MODAL ══════ */}
            {labOrderPatient && (
                <PlaceOrderModal
                    initialPatient={labOrderPatient}
                    onClose={() => setLabOrderPatient(null)}
                    onSuccess={() => setLabOrderPatient(null)}
                />
            )}
        </div>
    );
}
